/* eslint-disable indent */
/* eslint-disable @typescript-eslint/indent */
import { debounce } from 'lodash'
import { AxiosResponse, AxiosError } from 'axios'
export {}

/**
 * ===== Type Definitions =====
 */

// ===== Logan Definitions =====
interface ConfigOb {
  consoleLogan?: boolean
  disablePreReport?: boolean
  devMode?: boolean
  disableShake?: boolean
  reportTrigger?: boolean
  region?: string
  customReport?: {
    unionId?: string
    biz?: string
  }
  onReportFinished?: (reportResults: object, reportQuery?: object) => void
}

interface Logan {
  __version__: string
  config: (configOb: ConfigOb) => void
  log: (logString: string, logType?: string, logLevel?: 'info' | 'warn' | 'error', tags?: Record<string, string[]>) => void
  asyncLog: (logString: string, logType?: string, logLevel?: 'info' | 'warn' | 'error', tags?: Record<string, string[]>) => void
  report: () => void
}

declare global {
  interface Window {
    // @ts-ignore
    Logan: Logan
  }
}

// ===== Logan Api Report Definitions =====
export interface ApiReportObj {
  type: string
  url?: string
  method?: string
  traceid?: string
  traceId?: string
  region?: string
  locale?: string
  ua?: string
  params?: any
  requestData?: any
  responseStatus?: number
  responseData?: any
  [key: string]: any
}

type JSONValue = string | number | boolean | null | JSONValue[] | { [key: string]: JSONValue }

export type LoganApiTransformFn = (response: AxiosResponse, defaultReportObj: ApiReportObj) => JSONValue

export type LoganApiErrorTransformFn = (response: AxiosError, defaultReportObj: ApiReportObj) => JSONValue

// AxiosRequestConfig
declare module 'axios' {
  export interface AxiosRequestConfig {
    loganApiTransform?: LoganApiTransformFn
    loganApiErrorTransform?: LoganApiErrorTransformFn
  }
}

/**
 * ===== Utils =====
 */

// eslint-disable-next-line @utiljs/utiljs/no-same-function, @utiljs/utiljs/type-tostring-limit
const isObject = (obj: any) => Object.prototype.toString.call(obj) === '[object Object]'

const printError = (...args: any[]) => {
  try {
    console.warn(...args)
  } catch (error) {
    // do nothing
  }
}

// @ts-ignore
export const Logan: Logan = window?.Logan
  ? window.Logan
  : {
      __version__: '',
      config: () => undefined,
      log: () => undefined,
      asyncLog: () => undefined,
      report: () => undefined,
    }

export const log = (...args: Parameters<typeof Logan.log>) => {
  try {
    Logan.log(...args)
  } catch (error) {
    printError('Logan:log', error)
  }
}

export const asyncLog = (...args: Parameters<typeof Logan.log>) => {
  try {
    Logan.asyncLog(...args)
  } catch (error) {
    printError('Logan:log', error)
  }
}

export const report = (...args: Parameters<typeof Logan.report>) => {
  try {
    Logan.report(...args)
  } catch (error) {
    printError('Logan:report', error)
  }
}

export const reportDebounce = (function () {
  try {
    return debounce(report, 5 * 1000, { maxWait: 5 * 60 * 1000 }) as typeof report
  } catch (error) {
    printError('Logan:reportDebounce', error)
    return report
  }
})()

const MAX_RUN_LOG_COUNT = 500
const runLogHis = {
  count: 0,
}

export const logApi = (
  response: AxiosResponse,
  extra: {
    isAsyncLog?: boolean
    type?: string
    ignoreUrl?: string[]
  },
) => {
  try {
    const {
      // newline
      isAsyncLog = true,
      type = 'Ajax',
      ignoreUrl = [],
    } = isObject(extra) ? extra : {}

    if (ignoreUrl?.includes(response?.config?.url || '')) {
      return
    }

    const defaultReportObj: ApiReportObj = {
      type,
      url: response?.config?.url,
      method: response?.config?.method,
      traceid: response?.headers?.['m-traceid'],
      region: response?.config?.headers?.region || '',
      locale: response?.config?.headers?.locale || '',
      ua: window?.navigator?.userAgent || '',
      params: response?.config?.params,
      requestData: response?.config?.data,
      responseStatus: response?.status,
      responseData: response?.data,
    }

    let reportObj: JSONValue = defaultReportObj

    try {
      if (typeof response?.config?.loganApiTransform === 'function') {
        reportObj = response.config.loganApiTransform(response, defaultReportObj)
      }
    } catch (transformError) {
      printError('Logan:logApi transform error', transformError)
      reportObj = defaultReportObj
    }

    const reportLogType = 'api'
    const reportLogLevel = response?.data?.code !== 0 ? 'error' : 'info'
    const reportLogStr = JSON.stringify(reportObj)

    if (isAsyncLog) {
      asyncLog(reportLogStr, reportLogType, reportLogLevel)
      runLogHis.count += 1

      if (reportLogLevel === 'error' || runLogHis.count > MAX_RUN_LOG_COUNT) {
        setTimeout(function () {
          reportDebounce()
        }, 0)

        runLogHis.count = 0
      }
    } else {
      log(reportLogStr, reportLogType, reportLogLevel)
      runLogHis.count += 1

      if (reportLogLevel === 'error' || runLogHis.count > MAX_RUN_LOG_COUNT) {
        reportDebounce()
        runLogHis.count = 0
      }
    }
  } catch (error) {
    printError('Logan:logApiResponse', error)
  }
}

export const logApiError = (
  error: AxiosError,
  extra: {
    isAsyncLog?: boolean
    type?: string
    ignoreUrl?: string[]
  },
) => {
  try {
    const {
      // newline
      isAsyncLog = true,
      type = 'Ajax',
      ignoreUrl = [],
    } = isObject(extra) ? extra : {}

    if (ignoreUrl?.includes(error?.response?.config?.url || '')) {
      return
    }

    const traceId = error?.response?.headers?.['m-traceid'] || error?.request?.traceid || ''
    const method = error?.response?.config?.method || error?.request?.method || ''
    const url = error?.response?.config?.url || error?.request?.url || ''
    const ua = window?.navigator?.userAgent || ''
    const params = error?.response?.config?.params || ''
    const requestData = error?.response?.config?.data || ''
    const responseData = error?.response?.data || ''
    const responseStatus = error?.response?.status || error?.request?.status || -1

    const defaultReportObj: ApiReportObj = {
      type,
      traceId,
      url,
      method,
      ua,
      params,
      requestData,
      responseStatus,
      responseData,
    }

    let reportObj: JSONValue = defaultReportObj
    try {
      if (typeof error?.config?.loganApiErrorTransform === 'function') {
        reportObj = error.config.loganApiErrorTransform(error, defaultReportObj)
      }
    } catch (transformError) {
      printError('Logan:logApiError transform error', transformError)
      reportObj = defaultReportObj
    }

    const reportLogType = 'api'
    const reportLogLevel = 'error'
    const reportLogStr = JSON.stringify(reportObj)

    if (isAsyncLog) {
      asyncLog(reportLogStr, reportLogType, reportLogLevel)

      setTimeout(function () {
        reportDebounce()
      }, 0)
      runLogHis.count = 0
    } else {
      log(reportLogStr, reportLogType, reportLogLevel)

      reportDebounce()
      runLogHis.count = 0
    }
  } catch (error) {
    printError('Logan:logApiResponse', error)
  }
}
